import React, { useState, useCallback, useEffect, useRef } from "react";
import {
  View,
  Text,
  ScrollView,
  StyleSheet,
  Image,
  TextInput,
  TouchableOpacity,
  ActivityIndicator,
} from "react-native";
import { useFocusEffect } from "expo-router";
import { useSelector } from "react-redux";
import { RootState } from "../../store";
import i18n from "../../locales/i18n";
import customAxios from "../../utils/customAxios";
import WebSidebar from "./WebSidebar";

// Import the bills/receipt image for header
import ReceiptImage from "../../assets/images/stats.png";

// Import icons from web/stats folder
import PrinterIcon from "../../assets/web/stats/printer.png";

interface BillProduct {
  product_name: string;
  quantity: number;
  price_sell: string;
  type_quantity?: string;
}

interface Bill {
  id_facture: number;
  created_at: string;
  total: number;
  profit: number;
  capital?: number;
  facture?: BillProduct[];
  listofproducts?: BillProduct[];
}

interface GroupedBills {
  today: Bill[];
  yesterday: Bill[];
  older: Bill[];
}

// Calendar Picker Component
const CalendarPicker: React.FC<{
  label: string;
  selectedDate: string;
  onSelectDate: (date: string) => void;
  isRTL: boolean;
}> = ({ label, selectedDate, onSelectDate, isRTL }) => {
  const [viewDate, setViewDate] = useState(() => {
    if (selectedDate) return new Date(selectedDate);
    return new Date();
  });
  const [showYearPicker, setShowYearPicker] = useState(false);
  const [yearPickerVisible, setYearPickerVisible] = useState(false);
  const yearScrollRef = useRef<HTMLDivElement>(null);

  // Smooth toggle for year picker with auto-scroll
  useEffect(() => {
    if (showYearPicker) {
      requestAnimationFrame(() => setYearPickerVisible(true));
      setTimeout(() => {
        if (yearScrollRef.current) {
          const selected = yearScrollRef.current.querySelector(
            '[data-selected="true"]',
          );
          if (selected)
            selected.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }, 100);
    } else {
      setYearPickerVisible(false);
    }
  }, [showYearPicker]);

  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();

  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstDayOfMonth = new Date(year, month, 1).getDay();

  // Day names starting from Saturday (Algerian week)
  const dayNames = [
    i18n.t("saturday")?.slice(0, 3),
    i18n.t("sunday")?.slice(0, 3),
    i18n.t("monday")?.slice(0, 3),
    i18n.t("tuesday")?.slice(0, 3),
    i18n.t("wednesday")?.slice(0, 3),
    i18n.t("thursday")?.slice(0, 3),
    i18n.t("friday")?.slice(0, 3),
  ];

  const monthNames = [
    "Janvier",
    "Février",
    "Mars",
    "Avril",
    "Mai",
    "Juin",
    "Juillet",
    "Août",
    "Septembre",
    "Octobre",
    "Novembre",
    "Décembre",
  ];

  // Shift firstDay so Saturday=0
  const shiftedFirstDay = (firstDayOfMonth + 1) % 7;

  const days: (number | null)[] = [];
  for (let i = 0; i < shiftedFirstDay; i++) days.push(null);
  for (let i = 1; i <= daysInMonth; i++) days.push(i);

  const selectedParts = selectedDate ? selectedDate.split("-").map(Number) : [];
  const isSelected = (day: number) =>
    selectedParts.length === 3 &&
    selectedParts[0] === year &&
    selectedParts[1] === month + 1 &&
    selectedParts[2] === day;

  const isToday = (day: number) => {
    const today = new Date();
    return (
      today.getFullYear() === year &&
      today.getMonth() === month &&
      today.getDate() === day
    );
  };

  const handlePrevMonth = () => setViewDate(new Date(year, month - 1, 1));
  const handleNextMonth = () => setViewDate(new Date(year, month + 1, 1));

  const handleDayClick = (day: number) => {
    const dateStr = `${year}-${(month + 1).toString().padStart(2, "0")}-${day.toString().padStart(2, "0")}`;
    onSelectDate(dateStr);
  };

  const displayDate = selectedDate
    ? (() => {
        const d = new Date(selectedDate);
        return `${d.getDate().toString().padStart(2, "0")}/${(d.getMonth() + 1).toString().padStart(2, "0")}/${d.getFullYear()}`;
      })()
    : "--/--/----";

  // Generate year range (2015 to current year + 1)
  const currentYear = new Date().getFullYear();
  const years = Array.from(
    { length: currentYear - 2015 + 2 },
    (_, i) => 2015 + i,
  );

  return (
    <div style={{ flex: 1, minWidth: 260 }}>
      <div
        style={{
          fontSize: 14,
          fontWeight: "600",
          color: "#666",
          marginBottom: 8,
          textAlign: "center",
          fontFamily: "sans-serif",
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: 18,
          fontWeight: "700",
          color: selectedDate ? "#1379A2" : "#CCC",
          textAlign: "center",
          marginBottom: 12,
          fontFamily: "sans-serif",
        }}
      >
        {displayDate}
      </div>
      <div
        style={{
          backgroundColor: "#fff",
          borderRadius: 16,
          padding: 16,
          boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
          border: "1px solid #E8EDF2",
        }}
      >
        {/* Month/Year header - clickable for year picker */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 12,
          }}
        >
          <button
            onClick={handlePrevMonth}
            style={{
              background: "none",
              border: "none",
              fontSize: 20,
              cursor: "pointer",
              color: "#1379A2",
              padding: "4px 8px",
              borderRadius: 8,
            }}
          >
            ‹
          </button>
          <button
            onClick={() => setShowYearPicker(!showYearPicker)}
            style={{
              fontSize: 16,
              fontWeight: "700",
              color: "#333",
              fontFamily: "sans-serif",
              background: "none",
              border: "1px solid #E8EDF2",
              borderRadius: 8,
              padding: "4px 12px",
              cursor: "pointer",
              transition: "all 0.2s",
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLElement).style.backgroundColor =
                "#F0F7FA";
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLElement).style.backgroundColor =
                "transparent";
            }}
          >
            {monthNames[month]} {year} {showYearPicker ? "▴" : "▾"}
          </button>
          <button
            onClick={handleNextMonth}
            style={{
              background: "none",
              border: "none",
              fontSize: 20,
              cursor: "pointer",
              color: "#1379A2",
              padding: "4px 8px",
              borderRadius: 8,
            }}
          >
            ›
          </button>
        </div>

        {/* Year/Month grid picker */}
        {showYearPicker ? (
          <div style={{ marginBottom: 10 }}>
            {/* Year selector */}
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 4,
                justifyContent: "center",
                marginBottom: 10,
              }}
            >
              {years.map((y) => (
                <button
                  key={y}
                  onClick={() => {
                    setViewDate(new Date(y, month, 1));
                  }}
                  style={{
                    padding: "4px 8px",
                    borderRadius: 6,
                    fontSize: 12,
                    fontWeight: y === year ? "700" : "500",
                    border: "none",
                    cursor: "pointer",
                    transition: "all 0.15s",
                    backgroundColor: y === year ? "#1379A2" : "transparent",
                    color: y === year ? "#fff" : "#555",
                    minWidth: 48,
                  }}
                  onMouseEnter={(e) => {
                    if (y !== year)
                      (e.currentTarget as HTMLElement).style.backgroundColor =
                        "#F0F7FA";
                  }}
                  onMouseLeave={(e) => {
                    if (y !== year)
                      (e.currentTarget as HTMLElement).style.backgroundColor =
                        "transparent";
                  }}
                >
                  {y}
                </button>
              ))}
            </div>
            {/* Month selector */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(4, 1fr)",
                gap: 4,
              }}
            >
              {monthNames.map((m, i) => (
                <button
                  key={i}
                  onClick={() => {
                    setViewDate(new Date(year, i, 1));
                    setShowYearPicker(false);
                  }}
                  style={{
                    padding: "6px 4px",
                    borderRadius: 6,
                    fontSize: 11,
                    fontWeight: i === month ? "700" : "500",
                    border: "none",
                    cursor: "pointer",
                    transition: "all 0.15s",
                    backgroundColor: i === month ? "#1379A2" : "transparent",
                    color: i === month ? "#fff" : "#555",
                  }}
                  onMouseEnter={(e) => {
                    if (i !== month)
                      (e.currentTarget as HTMLElement).style.backgroundColor =
                        "#F0F7FA";
                  }}
                  onMouseLeave={(e) => {
                    if (i !== month)
                      (e.currentTarget as HTMLElement).style.backgroundColor =
                        "transparent";
                  }}
                >
                  {m.slice(0, 3)}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <>
            {/* Day names */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(7, 1fr)",
                gap: 2,
                marginBottom: 4,
              }}
            >
              {dayNames.map((d, i) => (
                <div
                  key={i}
                  style={{
                    textAlign: "center",
                    fontSize: 11,
                    fontWeight: "600",
                    color: "#999",
                    padding: "4px 0",
                    fontFamily: "sans-serif",
                  }}
                >
                  {d}
                </div>
              ))}
            </div>
            {/* Days grid */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(7, 1fr)",
                gap: 2,
              }}
            >
              {days.map((day, i) => (
                <div
                  key={i}
                  style={{ display: "flex", justifyContent: "center" }}
                >
                  {day ? (
                    <button
                      onClick={() => handleDayClick(day)}
                      style={{
                        width: 34,
                        height: 34,
                        borderRadius: 10,
                        border: "none",
                        cursor: "pointer",
                        fontSize: 13,
                        fontWeight: isSelected(day) ? "700" : "500",
                        fontFamily: "sans-serif",
                        backgroundColor: isSelected(day)
                          ? "#1379A2"
                          : isToday(day)
                            ? "#E8F4F8"
                            : "transparent",
                        color: isSelected(day)
                          ? "#fff"
                          : isToday(day)
                            ? "#1379A2"
                            : "#333",
                        transition: "all 0.15s",
                      }}
                      onMouseEnter={(e) => {
                        if (!isSelected(day))
                          e.currentTarget.style.backgroundColor = "#F0F7FA";
                      }}
                      onMouseLeave={(e) => {
                        if (!isSelected(day)) {
                          e.currentTarget.style.backgroundColor = isToday(day)
                            ? "#E8F4F8"
                            : "transparent";
                        }
                      }}
                    >
                      {day}
                    </button>
                  ) : (
                    <div style={{ width: 34, height: 34 }} />
                  )}
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const WebStatsScreen: React.FC = () => {
  const { locale } = useSelector((state: RootState) => state.app);
  const [bills, setBills] = useState<Bill[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedFilter, setSelectedFilter] = useState("today");
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [totalStats, setTotalStats] = useState({
    total: 0,
    profit: 0,
    capital: 0,
    count: 0,
  });

  // Check if current language is RTL (Arabic)
  const isRTL = locale === "ar" || i18n.locale === "ar";

  const dateFilters = [
    { label: i18n.t("tout"), value: "all" },
    { label: i18n.t("today"), value: "today" },
    { label: i18n.t("yesterday"), value: "yesterday" },
    { label: i18n.t("mounth"), value: "month" },
    { label: i18n.t("year"), value: "year" },
    { label: i18n.t("custom"), value: "custom" },
  ];

  // Format date for API: MM/DD/YYYY
  const formatDateForApiInterval = (dateStr: string): string => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return `${(date.getMonth() + 1).toString().padStart(2, "0")}/${date.getDate().toString().padStart(2, "0")}/${date.getFullYear()}`;
  };

  const getDateRangeForFilter = (
    filterValue: string,
  ): { start: string; end: string } | null => {
    const now = new Date();
    const fmt = (d: Date) =>
      `${(d.getMonth() + 1).toString().padStart(2, "0")}/${d.getDate().toString().padStart(2, "0")}/${d.getFullYear()}`;
    switch (filterValue) {
      case "today":
        return { start: fmt(now), end: fmt(now) };
      case "yesterday": {
        const y = new Date(now);
        y.setDate(y.getDate() - 1);
        return { start: fmt(y), end: fmt(y) };
      }
      case "month": {
        const s = new Date(now.getFullYear(), now.getMonth(), 1);
        return { start: fmt(s), end: fmt(now) };
      }
      case "year": {
        const s = new Date(now.getFullYear(), 0, 1);
        return { start: fmt(s), end: fmt(now) };
      }
      case "all":
      default:
        return null;
    }
  };

  const fetchBillsByDateRange = async (start: string, end: string) => {
    setIsLoading(true);
    try {
      const formattedStart = formatDateForApiInterval(start);
      const formattedEnd = formatDateForApiInterval(end);

      const response = await customAxios.get("/statistic_intervalle", {
        params: { start: formattedStart, end: formattedEnd },
        withCredentials: true,
      });

      if (
        response.data &&
        response.data.factures &&
        Array.isArray(response.data.factures)
      ) {
        setBills(response.data.factures);
        // Set total stats from response
        setTotalStats({
          total: response.data.total || 0,
          profit: response.data.totalprofit || 0,
          capital: response.data.totalcapital || 0,
          count:
            response.data.totalselles || response.data.factures.length || 0,
        });
      } else if (response.data && Array.isArray(response.data)) {
        setBills(response.data);
      } else {
        setBills([]);
      }
    } catch (error: any) {
      if (error?.response?.status === 404) {
        setBills([]);
      } else {
        setBills([]);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const fetchBills = async (filterValue: string = "today") => {
    setIsLoading(true);
    try {
      let range = getDateRangeForFilter(filterValue);

      // Always use /statistic_intervalle with query params
      if (!range) {
        const now = new Date();
        const fiveYearsAgo = new Date(
          now.getFullYear() - 5,
          now.getMonth(),
          now.getDate(),
        );
        const fmt = (d: Date) =>
          `${(d.getMonth() + 1).toString().padStart(2, "0")}/${d.getDate().toString().padStart(2, "0")}/${d.getFullYear()}`;
        range = { start: fmt(fiveYearsAgo), end: fmt(now) };
      }
      const response = await customAxios.get("/statistic_intervalle", {
        params: { start: range.start, end: range.end },
        withCredentials: true,
      });
      if (
        response.data &&
        response.data.factures &&
        Array.isArray(response.data.factures)
      ) {
        setBills(response.data.factures);
        // Set total stats from response
        setTotalStats({
          total: response.data.total || 0,
          profit: response.data.totalprofit || 0,
          capital: response.data.totalcapital || 0,
          count:
            response.data.totalselles || response.data.factures.length || 0,
        });
      } else if (response.data && Array.isArray(response.data)) {
        setBills(response.data);
      } else {
        setBills([]);
      }
    } catch (error: any) {
      if (error?.response?.status === 404) {
        setBills([]);
      } else {
        setBills([]);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleFilterChange = (filterValue: string) => {
    setSelectedFilter(filterValue);
    if (filterValue === "custom") {
      setShowDatePicker(true);
    } else {
      setShowDatePicker(false);
      fetchBills(filterValue);
    }
  };

  const handleDateRangeSearch = () => {
    if (startDate && endDate) {
      fetchBillsByDateRange(startDate, endDate);
    }
  };

  useFocusEffect(
    useCallback(() => {
      fetchBills(selectedFilter);
    }, []),
  );

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "-";
    return `${date.getDate().toString().padStart(2, "0")}/${(date.getMonth() + 1).toString().padStart(2, "0")}/${date.getFullYear()}`;
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "-";
    return `${date.getHours().toString().padStart(2, "0")}:${date.getMinutes().toString().padStart(2, "0")}`;
  };

  const isToday = (dateString: string) => {
    const date = new Date(dateString);
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const isYesterday = (dateString: string) => {
    const date = new Date(dateString);
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    return date.toDateString() === yesterday.toDateString();
  };

  // Filter bills based on search (by invoice number, total, product name, or date)
  const filteredBills = searchQuery.trim()
    ? bills.filter((b) => {
        const query = searchQuery.toLowerCase();
        const products = b.facture || b.listofproducts || [];
        const matchesProduct = products.some((p) =>
          p.product_name?.toLowerCase().includes(query),
        );
        return (
          b.id_facture?.toString().includes(query) ||
          b.total?.toString().includes(query) ||
          b.profit?.toString().includes(query) ||
          matchesProduct ||
          formatDate(b.created_at).includes(query)
        );
      })
    : bills;

  // Group bills by day
  const groupedBills: GroupedBills = {
    today: filteredBills.filter((b) => isToday(b.created_at)),
    yesterday: filteredBills.filter((b) => isYesterday(b.created_at)),
    older: filteredBills.filter(
      (b) => !isToday(b.created_at) && !isYesterday(b.created_at),
    ),
  };

  const handlePrint = (bill: Bill) => {
    // Get products list from either facture or listofproducts
    const products = bill.facture || bill.listofproducts || [];

    const printContent = `
      <html>
        <head>
          <meta charset="utf-8" />
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; direction: rtl; }
            .header { text-align: center; margin-bottom: 20px; }
            .bill-info { margin-bottom: 20px; }
            table { width: 100%; border-collapse: collapse; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: center; }
            th { background-color: #1379A2; color: white; }
            .total { font-size: 18px; font-weight: bold; margin-top: 20px; text-align: right; }
          </style>
        </head>
        <body>
          <div class="header">
            <h2>${i18n.t("invoiceNumber")} #${bill.id_facture}</h2>
            <p>${formatDate(bill.created_at)} ${formatTime(bill.created_at)}</p>
          </div>
          <table>
            <thead>
              <tr>
                <th>${i18n.t("productname")}</th>
                <th>${i18n.t("quantity")}</th>
                <th>${i18n.t("price")}</th>
              </tr>
            </thead>
            <tbody>
              ${products
                .map(
                  (p) => `
                <tr>
                  <td>${p.product_name}</td>
                  <td>${p.quantity}</td>
                  <td>${parseFloat(p.price_sell || "0").toLocaleString()} ${i18n.t("currency")}</td>
                </tr>
              `,
                )
                .join("")}
            </tbody>
          </table>
          <div class="total">
            ${i18n.t("total")}: ${(bill.total || 0).toLocaleString()} ${i18n.t("currency")}
          </div>
        </body>
      </html>
    `;

    const printWindow = window.open("", "_blank");
    if (printWindow) {
      printWindow.document.write(printContent);
      printWindow.document.close();
      printWindow.print();
    }
  };

  const renderBillCard = (bill: Bill) => (
    <View key={bill.id_facture} style={styles.billCard}>
      {/* Bill Header */}
      <View style={styles.billHeader}>
        <Text style={styles.billId}>#{bill.id_facture}</Text>
        <Text style={styles.billLabel}>{i18n.t("invoiceNumber")}</Text>
        <Text style={styles.billDateTime}>
          {formatDate(bill.created_at)} {formatTime(bill.created_at)}
        </Text>
      </View>

      {/* Bill Summary */}
      <View style={styles.billSummary}>
        <View style={styles.summaryItem}>
          <Text style={styles.summaryLabel}>{i18n.t("profit")}</Text>
          <Text style={styles.summaryValue}>
            {(bill.profit || 0).toLocaleString()} {i18n.t("currency")}
          </Text>
        </View>
        <View style={styles.summaryItem}>
          <Text style={styles.summaryLabel}>{i18n.t("total")}</Text>
          <Text style={[styles.summaryValue, styles.totalValue]}>
            {(bill.total || 0).toLocaleString()} {i18n.t("currency")}
          </Text>
        </View>
      </View>

      {/* Products Table Header */}
      <View style={styles.productsTableHeader}>
        <Text style={[styles.productHeaderCell, styles.priceCol]}>
          {i18n.t("price")}
        </Text>
        <Text style={[styles.productHeaderCell, styles.qtyCol]}>
          {i18n.t("quantity")}
        </Text>
        <Text style={[styles.productHeaderCell, styles.sizeCol]}>
          {i18n.t("typeOfQuantity")}
        </Text>
        <Text style={[styles.productHeaderCell, styles.nameCol]}>
          {i18n.t("productname")}
        </Text>
      </View>

      {/* Products List - Scrollable to show all items */}
      <ScrollView
        style={styles.productsScrollContainer}
        nestedScrollEnabled={true}
      >
        {(bill.facture || bill.listofproducts) &&
          (bill.facture || bill.listofproducts)!.map((product, index) => (
            <View
              key={index}
              style={[
                styles.productRow,
                index % 2 === 1 && styles.productRowAlt,
              ]}
            >
              <Text style={[styles.productCell, styles.priceCol]}>
                {parseFloat(product.price_sell || "0").toLocaleString()}{" "}
                {i18n.t("currency")}
              </Text>
              <Text style={[styles.productCell, styles.qtyCol]}>
                {product.quantity}
              </Text>
              <Text style={[styles.productCell, styles.sizeCol]}>
                {product.type_quantity || "-"}
              </Text>
              <Text
                style={[styles.productCell, styles.nameCol]}
                numberOfLines={1}
              >
                {product.product_name}
              </Text>
            </View>
          ))}
      </ScrollView>

      {/* Bill Actions - Print Only */}
      <View style={styles.billActions}>
        <TouchableOpacity
          style={styles.actionBtn}
          onPress={() => handlePrint(bill)}
        >
          <Image
            source={PrinterIcon}
            style={styles.actionIcon}
            resizeMode="contain"
          />
        </TouchableOpacity>
      </View>
    </View>
  );

  const renderBillsSection = (title: string, billsList: Bill[]) => {
    if (billsList.length === 0) return null;

    return (
      <View style={styles.sectionContainer}>
        <Text style={styles.sectionTitle}>{title}</Text>
        <View style={styles.billsGrid}>
          {billsList.map((bill) => renderBillCard(bill))}
        </View>
      </View>
    );
  };

  return (
    <div
      key={`stats-screen-${locale}`}
      className="flex flex-1 bg-[#F5F5F5] min-h-screen flex-row"
    >
      <WebSidebar />

      <View style={styles.mainContent}>
        <ScrollView
          style={styles.scrollView}
          showsVerticalScrollIndicator={false}
        >
          {/* Header with blue bar */}
          <View style={styles.headerSection}>
            <div
              className={`flex flex-row items-center gap-4 ${isRTL ? "justify-end flex-row-reverse" : "justify-start"}`}
            >
              <View style={styles.headerIconContainer}>
                <Image
                  source={ReceiptImage}
                  style={styles.headerIcon}
                  resizeMode="contain"
                />
              </View>
              <Text style={styles.headerTitle}>{i18n.t("bills")}</Text>
            </div>
            <View style={styles.headerBar} />
          </View>

          {/* Date Filter Tabs */}
          <View style={styles.dateFilterRow}>
            {dateFilters.map((filter) => (
              <TouchableOpacity
                key={filter.value}
                style={[
                  styles.dateFilterTab,
                  selectedFilter === filter.value && styles.dateFilterTabActive,
                ]}
                onPress={() => handleFilterChange(filter.value)}
              >
                <Text
                  style={[
                    styles.dateFilterText,
                    selectedFilter === filter.value &&
                      styles.dateFilterTextActive,
                  ]}
                >
                  {filter.label}
                </Text>
              </TouchableOpacity>
            ))}
          </View>

          {/* Custom Date Range Picker with Calendar */}
          {showDatePicker && (
            <View style={styles.datePickerContainer}>
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  gap: 24,
                  justifyContent: "center",
                  alignItems: "flex-start",
                  flexWrap: "wrap",
                }}
              >
                <CalendarPicker
                  label={i18n.t("startDate")}
                  selectedDate={startDate}
                  onSelectDate={setStartDate}
                  isRTL={isRTL}
                />
                <CalendarPicker
                  label={i18n.t("endDate")}
                  selectedDate={endDate}
                  onSelectDate={setEndDate}
                  isRTL={isRTL}
                />
              </div>
              <View style={{ alignItems: "center", marginTop: 16 }}>
                <TouchableOpacity
                  style={[
                    styles.searchDateButton,
                    (!startDate || !endDate) && { opacity: 0.5 },
                  ]}
                  onPress={handleDateRangeSearch}
                  disabled={!startDate || !endDate}
                >
                  <Text style={styles.searchDateButtonText}>
                    {i18n.t("search")}
                  </Text>
                </TouchableOpacity>
              </View>
            </View>
          )}

          {/* Stats Summary Cards */}
          {bills.length > 0 && (
            <View style={styles.statsSummary}>
              <View style={styles.statCard}>
                <Text style={styles.statLabel}>{i18n.t("totalSales")}</Text>
                <Text style={styles.statValue}>
                  {totalStats.total.toLocaleString()} {i18n.t("currency")}
                </Text>
              </View>
              <View style={styles.statCard}>
                <Text style={styles.statLabel}>{i18n.t("totalProfit")}</Text>
                <Text style={[styles.statValue, { color: "#22C55E" }]}>
                  {totalStats.profit.toLocaleString()} {i18n.t("currency")}
                </Text>
              </View>
              <View style={styles.statCard}>
                <Text style={styles.statLabel}>{i18n.t("totalCapital")}</Text>
                <Text style={styles.statValue}>
                  {totalStats.capital.toLocaleString()} {i18n.t("currency")}
                </Text>
              </View>
              <View style={styles.statCard}>
                <Text style={styles.statLabel}>{i18n.t("totalBills")}</Text>
                <Text style={styles.statValue}>{totalStats.count}</Text>
              </View>
            </View>
          )}

          {/* Search Bar */}
          <View style={styles.controlsRow}>
            <View style={styles.searchContainer}>
              <TextInput
                style={styles.searchInput}
                placeholder={i18n.t("searchInvoice")}
                value={searchQuery}
                onChangeText={setSearchQuery}
                placeholderTextColor="#999"
              />
            </View>
          </View>

          {/* Bills Content */}
          {isLoading ? (
            <View style={styles.loadingContainer}>
              <ActivityIndicator size="large" color="#1379A2" />
            </View>
          ) : filteredBills.length === 0 ? (
            <View style={styles.emptyContainer}>
              <Text style={styles.emptyText}>{i18n.t("nobills")}</Text>
            </View>
          ) : (
            <View>
              {renderBillsSection(i18n.t("today"), groupedBills.today)}
              {renderBillsSection(i18n.t("yesterday"), groupedBills.yesterday)}
              {renderBillsSection(i18n.t("older"), groupedBills.older)}
            </View>
          )}
        </ScrollView>
      </View>
    </div>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#F5F5F5",
    minHeight: "100vh" as any,
  },
  mainContent: {
    flex: 1,
  },
  containerRTL: {
    flexDirection: "row-reverse",
  } as any,
  scrollView: {
    flex: 1,
    padding: 20,
  },
  headerSection: {
    marginBottom: 20,
  },
  header: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: 16,
    paddingVertical: 16,
    paddingHorizontal: 24,
  },
  headerIconContainer: {
    width: 100,
    height: 100,
    borderRadius: 50,
    backgroundColor: "#E8F4F8",
    borderWidth: 4,
    borderColor: "#1379A2",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
  },
  headerTitle: {
    fontSize: 32,
    fontWeight: "bold",
    color: "#333",
  },
  headerIcon: {
    width: 70,
    height: 70,
  },
  headerBar: {
    height: 8,
    backgroundColor: "#1379A2",
    borderRadius: 4,
    marginTop: 8,
  },
  controlsRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
    marginBottom: 20,
  },
  filterButton: {
    width: 44,
    height: 44,
    borderRadius: 8,
    backgroundColor: "#FFFFFF",
    borderWidth: 1,
    borderColor: "#E0E0E0",
    alignItems: "center",
    justifyContent: "center",
  },
  filterIconImg: {
    width: 24,
    height: 24,
  },
  calendarButton: {
    width: 44,
    height: 44,
    borderRadius: 8,
    backgroundColor: "#1379A2",
    alignItems: "center",
    justifyContent: "center",
  },
  calendarIconImg: {
    width: 24,
    height: 24,
    tintColor: "#FFFFFF",
  },
  searchContainer: {
    flex: 1,
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#FFFFFF",
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#E0E0E0",
    paddingHorizontal: 12,
  },
  searchInput: {
    flex: 1,
    paddingVertical: 12,
    fontSize: 18,
    textAlign: "right",
  },
  searchIcon: {
    fontSize: 20,
    marginLeft: 8,
  },
  sectionContainer: {
    marginBottom: 24,
  },
  sectionTitle: {
    fontSize: 22,
    fontWeight: "600",
    color: "#333",
    textAlign: "right",
    marginBottom: 16,
  },
  billsGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 16,
  },
  billCard: {
    width: 320,
    backgroundColor: "#FFFFFF",
    borderRadius: 12,
    padding: 16,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.08,
    shadowRadius: 4,
    elevation: 2,
  },
  billHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: 8,
    marginBottom: 12,
    paddingBottom: 8,
    borderBottomWidth: 1,
    borderBottomColor: "#E0E0E0",
  },
  billId: {
    fontSize: 16,
    color: "#1379A2",
    fontWeight: "500",
  },
  billLabel: {
    fontSize: 14,
    color: "#1379A2",
  },
  billDateTime: {
    fontSize: 14,
    color: "#666",
  },
  billSummary: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 12,
  },
  summaryItem: {
    alignItems: "center",
  },
  summaryLabel: {
    fontSize: 14,
    color: "#666",
    marginBottom: 4,
  },
  summaryValue: {
    fontSize: 18,
    fontWeight: "600",
    color: "#333",
  },
  totalValue: {
    color: "#1379A2",
  },
  productsTableHeader: {
    flexDirection: "row",
    paddingVertical: 8,
    borderTopWidth: 1,
    borderBottomWidth: 1,
    borderColor: "#E0E0E0",
  },
  productHeaderCell: {
    fontSize: 13,
    fontWeight: "600",
    color: "#1379A2",
    textAlign: "center",
  },
  productsScrollContainer: {
    maxHeight: 200,
  },
  productRow: {
    flexDirection: "row",
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: "#F0F0F0",
  },
  productRowAlt: {
    backgroundColor: "#FAFAFA",
  },
  productCell: {
    fontSize: 14,
    color: "#333",
    textAlign: "center",
  },
  nameCol: {
    flex: 2,
    textAlign: "right",
  },
  sizeCol: {
    flex: 1,
  },
  qtyCol: {
    flex: 1,
  },
  priceCol: {
    flex: 1.5,
  },
  billActions: {
    flexDirection: "row",
    justifyContent: "center",
    gap: 16,
    marginTop: 12,
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: "#E0E0E0",
  },
  actionBtn: {
    width: 36,
    height: 36,
    borderRadius: 8,
    backgroundColor: "#F5F5F5",
    alignItems: "center",
    justifyContent: "center",
  },
  actionIcon: {
    width: 20,
    height: 20,
  },
  loadingContainer: {
    padding: 60,
    alignItems: "center",
  },
  emptyContainer: {
    padding: 60,
    alignItems: "center",
  },
  emptyText: {
    color: "#999",
    fontSize: 18,
  },
  dateFilterRow: {
    flexDirection: "row",
    justifyContent: "center",
    gap: 8,
    marginBottom: 16,
    backgroundColor: "#FFFFFF",
    borderRadius: 30,
    padding: 8,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.05,
    shadowRadius: 4,
    elevation: 2,
  },
  dateFilterTab: {
    paddingHorizontal: 20,
    paddingVertical: 10,
    borderRadius: 20,
    backgroundColor: "transparent",
  },
  dateFilterTabActive: {
    backgroundColor: "#1379A2",
  },
  dateFilterText: {
    fontSize: 16,
    color: "#666",
    fontWeight: "500",
  },
  dateFilterTextActive: {
    color: "#FFFFFF",
    fontWeight: "600",
  },
  datePickerContainer: {
    backgroundColor: "#FFFFFF",
    borderRadius: 12,
    padding: 16,
    marginBottom: 16,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.05,
    shadowRadius: 4,
    elevation: 2,
  },
  datePickerRow: {
    flexDirection: "row",
    alignItems: "flex-end",
    justifyContent: "center",
    gap: 16,
    flexWrap: "wrap",
  },
  dateInputGroup: {
    alignItems: "center",
    gap: 8,
  },
  dateLabel: {
    fontSize: 14,
    color: "#666",
    fontWeight: "500",
  },
  searchDateButton: {
    backgroundColor: "#1379A2",
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 8,
  },
  searchDateButtonText: {
    color: "#FFFFFF",
    fontSize: 16,
    fontWeight: "600",
  },
  statsSummary: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 16,
    marginBottom: 20,
    justifyContent: "center",
  },
  statCard: {
    backgroundColor: "#FFFFFF",
    borderRadius: 12,
    padding: 16,
    minWidth: 180,
    alignItems: "center",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.05,
    shadowRadius: 4,
    elevation: 2,
    borderLeftWidth: 4,
    borderLeftColor: "#1379A2",
  },
  statLabel: {
    fontSize: 14,
    color: "#666",
    marginBottom: 8,
  },
  statValue: {
    fontSize: 22,
    fontWeight: "bold",
    color: "#333",
  },
});

export default WebStatsScreen;
